import { ButtonComponent } from 'baseComponents/ButtonComponent';
import dayjs, { Dayjs } from 'dayjs';
import { TableComponent } from 'baseComponents/TableComponent';
import { AdminErrorPage } from 'components/AdminErrorPage';
import { useState } from 'react';
import { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router';
import { queryClient } from 'constants/query-client';
import { Col, notification } from 'antd';
import {
  ScheduledCampaign,
  useGetScheduledCampaigns,
} from 'hooks/useGetScheduledCampaigns';
import { useDeleteCampaign } from 'hooks/useDeleteCampaign';
import { useEditCampaign } from 'hooks/useEditCampaign';
import { RemoveCampaignModal } from './components/RemoveCampaignModal';
import { ScreenWrapperComponent } from 'components/layouts/ScreenWrapperComponent';
import { EditCampaignModal } from './components/EditCampaignModal';
import { convertSendDate } from 'helpers/create-campaign';
import {
  NotEnoughCreditsData,
  NotEnoughCreditsModal,
} from './components/NotEnoughCreditsModal';

const columns: ColumnsType<ScheduledCampaign> = [
  {
    title: 'Campaign Name',
    dataIndex: 'name',
    key: 'name',
  },
  {
    title: 'Scheduled Date',
    dataIndex: 'scheduled',
    key: 'scheduled',
  },
  {
    title: 'Channel',
    dataIndex: 'channel',
    key: 'channel',
  },
  {
    title: 'Estimated Audience',
    dataIndex: 'audience_estimated',
    key: 'audience_estimated',
  },
];

export const ScheduledCampaigns = () => {
  const [api, contextHolder] = notification.useNotification();
  const navigate = useNavigate();

  const [selectedCampaigns, setSelectedCampaigns] = useState<
    ScheduledCampaign[]
  >([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);

  const [notEnougCreditsData, setNotEnoughCreditsData] =
    useState<NotEnoughCreditsData>();
  const [date, setDate] = useState<Dayjs | null>(null);
  const [time, setTime] = useState<Dayjs | null>(null);

  const { mutate: deleteCampaign, isPending: isDeletePending } =
    useDeleteCampaign({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['campaigns', 'scheduled'],
        });

        navigate('/scheduled-campaigns');
        setDeleteModalOpen(false);
      },
      onError(error) {
        api.error({
          message: 'Something went wrong',
          description:
            error.message ||
            'Something went wrong with removing campaign. Please try again later.',
          duration: 3,
        });
        setDeleteModalOpen(false);
      },
    });

  const { mutate: editCampaign, isPending: isEditPending } = useEditCampaign({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['campaigns', 'scheduled'],
      });

      navigate('/scheduled-campaigns');
      setEditModalOpen(false);
      api.success({
        message: 'Campaign edited',
        description: 'Campaign has beed successfully edited.',
        duration: 3,
      });
    },
    onError(error) {
      api.error({
        message: 'Something went wrong',
        description:
          error.message ||
          'Something went wrong with editing campaign. Please try again later.',
        duration: 3,
      });

      setEditModalOpen(false);
      if (error.response?.status === 400) {
        setNotEnoughCreditsData(
          error.response.data.data as unknown as NotEnoughCreditsData
        );
      }
    },
  });

  const {
    data = [],
    isLoading,
    isRefetching,
    isError,
    error,
    refetch,
  } = useGetScheduledCampaigns();

  const handleDeleteCampaign = () => {
    deleteCampaign(
      selectedCampaigns.map(({ schedule_id }) => schedule_id.toString())
    );
  };

  const parsedScheduledCampaignsData = () => {
    if (data) {
      return data.map((obj) => {
        return {
          ...obj,
          // todo: below is a hack for displaying the date in UTC, since BE is returning data without Z at the end.
          // if there will be problem on BE with fixing it, uncomment below.
          // scheduled: dayjs(`${obj.scheduled}Z`).format('YYYY-MM-DD HH:mm'),
          scheduled: dayjs(obj.scheduled).format('YYYY-MM-DD HH:mm'),
          key: obj.schedule_id,
        };
      });
    }
    return [];
  };

  const handleEditCampaign = () => {
    editCampaign({
      schedule_id: selectedCampaigns[0].schedule_id,
      data: {
        scheduled_time: convertSendDate(date, time) || 0,
      },
    });
  };

  if (isError) {
    return (
      <AdminErrorPage
        isRefetching={isRefetching}
        refetch={refetch}
        error={error.response?.data?.message || 'Error fetching campaigns'}
      />
    );
  }

  const handleSelection = (selectedCampaigns: ScheduledCampaign[]) => {
    setSelectedCampaigns(selectedCampaigns);
    setDate(dayjs(selectedCampaigns[0].scheduled));
    setTime(dayjs(selectedCampaigns[0].scheduled));
  };

  return (
    <ScreenWrapperComponent>
      {contextHolder}
      <Col md={{ pull: 3, push: 3, span: 18 }}>
        <TableComponent
          loading={
            isLoading || isRefetching || isEditPending || isDeletePending
          }
          dataSource={parsedScheduledCampaignsData()}
          total={data.length}
          columns={columns}
          selectable
          onSelection={(_, selectedCampaigns) => {
            handleSelection(selectedCampaigns);
          }}
          ctaButtons={
            <>
              <ButtonComponent
                label='Delete'
                onClick={() => {
                  setDeleteModalOpen(true);
                }}
                appereance='secondary'
                size='middle'
                style={{ maxWidth: 320 }}
                disabled={selectedCampaigns.length === 0}
              />
              <ButtonComponent
                label='Edit'
                onClick={() => {
                  setEditModalOpen(true);
                }}
                appereance='primary'
                size='middle'
                style={{ maxWidth: 320 }}
                disabled={selectedCampaigns.length === 0}
              />
            </>
          }
        />
        <RemoveCampaignModal
          isModalOpen={deleteModalOpen}
          closeModal={() => setDeleteModalOpen(false)}
          handleDeleteCampaign={handleDeleteCampaign}
        />
        <EditCampaignModal
          isModalOpen={editModalOpen}
          closeModal={() => setEditModalOpen(false)}
          handleDeleteCampaign={handleEditCampaign}
          date={date}
          time={time}
          setDate={setDate}
          setTime={setTime}
        />
        <NotEnoughCreditsModal
          onCancel={() => setNotEnoughCreditsData(undefined)}
          onAction={() => navigate('/purchase-credits')}
          data={notEnougCreditsData}
        />
      </Col>
    </ScreenWrapperComponent>
  );
};
